import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Modal,
  Form,
  Input,
  Checkbox,
  Button as AntButton,
  message,
} from 'antd';
import { MaskedInput } from 'antd-mask-input';
import { useSelector } from 'react-redux';
import { RootState } from '@/services/store';
import { useGetShoppingCartDetailsQuery } from '@/shoppingCart/services/shopping-cart.service';
import { ClientStorageService } from '@/common/services/client-storage.service';

import { DiContainer } from '@/di/di-container';
import LogoImage from './icons/logo.svg';
import { Button } from '@/configurator/components/shared';
import { useGetProfileQuery } from '@/profile/services/profile.service';
import { useCreateCallbackMutation } from '@/checkout/services/lead.service';

const clientStorageService = DiContainer.get(ClientStorageService);

export function PrimaryLayoutHeader() {
  const sessionId = clientStorageService.getUUID();

  const { refetch } = useGetShoppingCartDetailsQuery({
    session: sessionId,
    sharedId: new URLSearchParams(window.location.search).get('shared_uuid'),
  });

  // Профиль нужен только авторизованному (SSO) — гость его не запрашивает,
  // иначе на каждой странице /profile отдаёт 403 (шум в консоли, баг 27).
  const { isAuthorized } = useSelector((state: RootState) => state.auth);
  const { data: profileData } = useGetProfileQuery(sessionId, {
    skip: !isAuthorized,
  });

  if (profileData) {
    localStorage.setItem('owner_uuid', profileData.data.attributes.uuid);
  }

  // Форма обратного звонка → лид «Обратный звонок» в Битрикс24.
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [createCallback, { isLoading: sending }] = useCreateCallbackMutation();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onFinish = async (values: any) => {
    const res = await createCallback({
      session: sessionId,
      callback: {
        full_name: values.full_name,
        phone: values.phone,
        comment: values.comment,
      },
    });
    if ('data' in res) {
      message.success('Заявка принята — менеджер перезвонит вам.');
      setOpen(false);
      form.resetFields();
    } else {
      message.error('Не удалось отправить заявку. Попробуйте ещё раз.');
    }
  };

  useEffect(() => {
    document.addEventListener('onShoppingCartUpdate', refetch);
    return () => {
      document.removeEventListener('onShoppingCartUpdate', refetch);
    };
  }, []);

  return (
    <div className="header w-full ">
      <div className="header__inner mx-auto max-w-[1440px] w-[90%] flex justify-between items-center py-[16px]">
        <div className="header__col flex flex-wrap items-center gap-x-4 gap-y-2">
          <Link className="logo__button inline-block" to="/">
            <img width={100} height={28} src={LogoImage} />
          </Link>
          <a
            className="phone_number hover:text-black whitespace-nowrap"
            href="tel:+788005559983"
            target="_parent">
            8 (800) 555 99 83
          </a>
          <Button
            className="call_button px-[16px] py-[8px] text-[16px] font-medium"
            onClick={() => setOpen(true)}>
            Обратный звонок
          </Button>
        </div>
      </div>

      <Modal
        title="Заказать обратный звонок"
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={440}
        destroyOnClose>
        <p className="text-[#666] mb-4">
          Оставьте телефон — менеджер перезвонит и ответит на ваши вопросы.
        </p>
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item name="full_name" label="Имя">
            <Input className="h-[44px]" placeholder="Как к вам обращаться" />
          </Form.Item>
          <Form.Item
            name="phone"
            label="Телефон"
            rules={[
              {
                required: true,
                message: 'Укажите телефон',
                min: 11,
                transform: (v: string) => (v || '').replace(/\D/g, ''),
              },
            ]}>
            <MaskedInput mask={'+7(000)000-00-00'} className="h-[44px]" />
          </Form.Item>
          <Form.Item name="comment" label="Комментарий">
            <Input.TextArea rows={2} placeholder="Удобное время, вопрос и т.п." />
          </Form.Item>
          <Form.Item
            name="consent"
            valuePropName="checked"
            rules={[
              {
                validator: (_, v) =>
                  v
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error('Необходимо согласие на обработку данных')
                      ),
              },
            ]}>
            <Checkbox>
              Я согласен на обработку персональных данных в соответствии с{' '}
              <a
                href="https://liliani.ru/policy/"
                target="_blank"
                rel="noreferrer"
                className="text-[#007E3C] underline">
                политикой
              </a>
            </Checkbox>
          </Form.Item>
          <AntButton
            type="primary"
            htmlType="submit"
            block
            size="large"
            loading={sending}
            style={{ background: '#66B28A', height: 48 }}>
            Жду звонка
          </AntButton>
        </Form>
      </Modal>
    </div>
  );
}
